import React, { useState, useEffect, useRef } from 'react';
import { axiosInstance } from '../config';
import { useNavigate } from 'react-router-dom';
import IngredientAutocomplete from '../components/IngredientAutocomplete';
import { formatNutritionValue } from '../utils/nutritionCalculator';
import '../styles/mealPlanning.css';
import '../styles/mealPlanningModern.css';

const MealPlanningNew = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('create');
    const [addedToPlanToast, setAddedToPlanToast] = useState(false);
    const [publicSearchQuery, setPublicSearchQuery] = useState('');
    const [publicPlans, setPublicPlans] = useState([]);
    const [loadingPublicPlans, setLoadingPublicPlans] = useState(false);
    const [publicPlansLoaded, setPublicPlansLoaded] = useState(false);
    const addedToastTimeoutRef = useRef(null);
    const [searchType, setSearchType] = useState('name');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selectedRecipes, setSelectedRecipes] = useState([]);
    const [mealPlanName, setMealPlanName] = useState('');
    const [mealPlanDescription, setMealPlanDescription] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [mealPlans, setMealPlans] = useState([]);
    const [loadingPlans, setLoadingPlans] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [nutritionPreview, setNutritionPreview] = useState(null);
    const [loadingNutritionPreview, setLoadingNutritionPreview] = useState(false);
    const [allergens, setAllergens] = useState([]);
    const [dislikedFoods, setDislikedFoods] = useState([]);
    const [mealPrepDraftRecipes, setMealPrepDraftRecipes] = useState([]);
    const [loadingMealPrepDraft, setLoadingMealPrepDraft] = useState(false);
    
    useEffect(() => {
        if (activeTab === 'view') {
            loadMealPlans();
        }
    }, [activeTab]);

    useEffect(() => {
        if (activeTab === 'community' && !publicPlansLoaded) {
            searchPublicPlans('');
        }
    }, [activeTab]);

    useEffect(() => {
        if (activeTab === 'create') {
            loadMealPrepDraft();
        }
    }, [activeTab]);
    
    useEffect(() => {
        // Load preferences (allergens and disliked foods) when component mounts
        loadUserPreferences();
    }, []);
    
    useEffect(() => {
        if (selectedRecipes.length > 0) {
            loadPreview();
            loadNutritionPreview();
        } else {
            setPreviewData(null);
            setNutritionPreview(null);
        }
    }, [selectedRecipes]);

    useEffect(() => {
        return () => {
            if (addedToastTimeoutRef.current) clearTimeout(addedToastTimeoutRef.current);
        };
    }, []);

    const loadMealPlans = async () => {
        setLoadingPlans(true);
        try {
            const response = await axiosInstance.get('/meal-planning/plans');
            if (response.data.success) {
                setMealPlans(response.data.mealPlans);
            }
        } catch (error) {
            console.error('Error loading meal plans:', error);
            alert('Failed to load meal plans');
        } finally {
            setLoadingPlans(false);
        }
    };

    const loadMealPrepDraft = async () => {
        const userId = window.localStorage.getItem('userID');
        if (!userId) {
            setMealPrepDraftRecipes([]);
            return;
        }
        setLoadingMealPrepDraft(true);
        try {
            const response = await axiosInstance.get(`/recipes/meal-prep-draft/${userId}`);
            if (response.data && response.data.mealPrepDraft) {
                setMealPrepDraftRecipes(response.data.mealPrepDraft);
            } else {
                setMealPrepDraftRecipes([]);
            }
        } catch (error) {
            console.error('Error loading meal prep draft:', error);
            setMealPrepDraftRecipes([]);
        } finally {
            setLoadingMealPrepDraft(false);
        }
    };

    const loadUserPreferences = async () => {
        try {
            const response = await axiosInstance.get('/meal-planning/preferences');
            if (response.data.success && response.data.preferences) {
                const prefs = response.data.preferences;
                if (prefs.allergies) {
                    setAllergens(prefs.allergies);
                }
                if (prefs.dislikedFoods) {
                    setDislikedFoods(prefs.dislikedFoods);
                }
            }
        } catch (error) {
            console.error('Error loading preferences:', error);
            // Don't show error to user, just log it
        }
    };

    const searchPublicPlans = async (q) => {
        setLoadingPublicPlans(true);
        try {
            const response = await axiosInstance.get('/meal-planning/public', { params: { q, limit: 30 } });
            if (response.data.success) {
                setPublicPlans(response.data.plans);
                setPublicPlansLoaded(true);
            }
        } catch (error) {
            console.error('Error searching public plans:', error);
        } finally {
            setLoadingPublicPlans(false);
        }
    };

    const handlePublicSearch = (e) => {
        e.preventDefault();
        searchPublicPlans(publicSearchQuery);
    };

    const filterRecipesByAllergens = (recipes) => {
        const excludedIngredients = [...allergens, ...dislikedFoods];
        
        if (excludedIngredients.length === 0) {
            return recipes;
        }

        return recipes.filter(recipe => {
            // Check if recipe ingredients contain any allergens or disliked foods
            const hasExcludedIngredient = recipe.ingredients?.some(ingredient => {
                const ingredientName = ingredient.name.toLowerCase();
                return excludedIngredients.some(excluded => 
                    ingredientName.includes(excluded.toLowerCase()) ||
                    excluded.toLowerCase().includes(ingredientName)
                );
            });

            return !hasExcludedIngredient;
        });
    };

    const searchRecipes = async () => {
        if (!searchQuery.trim()) {
            alert('Please enter a search query');
            return;
        }

        setSearching(true);
        try {
            const userId = window.localStorage.getItem('userID');
            let response;
            
            if (searchType === 'name') {
                // Use the regular recipes search endpoint with cost calculation
                response = await axiosInstance.get('/recipes/search', {
                    params: { 
                        name: searchQuery, 
                        calculateCosts: 'true',
                        userId: userId,
                        limit: 30 
                    }
                });
                
                if (response.data && Array.isArray(response.data)) {
                    const filteredRecipes = filterRecipesByAllergens(response.data);
                    setSearchResults(filteredRecipes);
                    
                    if (filteredRecipes.length < response.data.length) {
                        const count = response.data.length - filteredRecipes.length;
                        alert(`Filtered out ${count} recipe(s) containing allergens or disliked ingredients (set in Preferences)`);
                    }
                }
            } else {
                // Search by ingredient using the regular recipes search endpoint
                response = await axiosInstance.get('/recipes/search', {
                    params: { 
                        ingredients: searchQuery,
                        calculateCosts: 'true',
                        userId: userId,
                        limit: 30 
                    }
                });
                
                if (response.data && Array.isArray(response.data)) {
                    const filteredRecipes = filterRecipesByAllergens(response.data);
                    setSearchResults(filteredRecipes);
                    
                    if (filteredRecipes.length < response.data.length) {
                        const count = response.data.length - filteredRecipes.length;
                        alert(`Filtered out ${count} recipe(s) containing allergens or disliked ingredients (set in Preferences)`);
                    }
                }
            }
        } catch (error) {
            console.error('Error searching recipes:', error);
            alert('Failed to search recipes');
        } finally {
            setSearching(false);
        }
    };

    const searchFromCupboard = async () => {
        setSearching(true);
        try {
            const userId = window.localStorage.getItem('userID');
            
            // Get cupboard items first
            const cupboardResponse = await axiosInstance.get(`/cupboard/${userId}`);
            
            if (cupboardResponse.data.success && cupboardResponse.data.items.length > 0) {
                // Get ingredient names from cupboard
                const cupboardIngredientNames = cupboardResponse.data.items.map(item => item.ingredientName);
                
                // Search recipes using cupboard ingredients
                const response = await axiosInstance.get('/recipes/search', {
                    params: { 
                        ingredients: cupboardIngredientNames.join(','),
                        calculateCosts: 'true',
                        userId: userId,
                        limit: 30 
                    }
                });
                
                if (response.data && Array.isArray(response.data)) {
                    const filteredRecipes = filterRecipesByAllergens(response.data);
                    setSearchResults(filteredRecipes);
                    setSearchType('cupboard');
                    
                    if (filteredRecipes.length < response.data.length) {
                        const count = response.data.length - filteredRecipes.length;
                        alert(`Filtered out ${count} recipe(s) containing allergens or disliked ingredients (set in Preferences)`);
                    }
                }
            } else {
                alert('Your cupboard is empty. Add ingredients to your cupboard first.');
                setSearchResults([]);
            }
        } catch (error) {
            console.error('Error searching from cupboard:', error);
            alert('Failed to search recipes from cupboard');
        } finally {
            setSearching(false);
        }
    };

    const addRecipeToMealPlan = (recipe) => {
        if (selectedRecipes.find(r => r.recipeId === recipe._id)) {
            alert('This recipe is already in your meal plan');
            return;
        }

        const newRecipe = {
            recipeId: recipe._id,
            recipeName: recipe.name,
            recipeImage: recipe.imageUrl,
            servings: recipe.servings,
            originalServings: recipe.servings,
            cookingTime: recipe.cookingTime,
            mealType: 'dinner'
        };

        setSelectedRecipes([...selectedRecipes, newRecipe]);

        // Show "Added To Meal Plan" toast for 2 seconds
        if (addedToastTimeoutRef.current) clearTimeout(addedToastTimeoutRef.current);
        setAddedToPlanToast(true);
        addedToastTimeoutRef.current = setTimeout(() => {
            setAddedToPlanToast(false);
            addedToastTimeoutRef.current = null;
        }, 2000);
    };

    const removeRecipeFromMealPlan = (recipeId) => {
        setSelectedRecipes(selectedRecipes.filter(r => r.recipeId !== recipeId));
    };

    const updateRecipeServings = (recipeId, newServings) => {
        const servings = parseInt(newServings);
        if (servings < 1) return;

        setSelectedRecipes(selectedRecipes.map(r => 
            r.recipeId === recipeId ? { ...r, servings } : r
        ));
    };

    const updateRecipeMealType = (recipeId, mealType) => {
        setSelectedRecipes(selectedRecipes.map(r => 
            r.recipeId === recipeId ? { ...r, mealType } : r
        ));
    };

    const loadPreview = async () => {
        if (selectedRecipes.length === 0) return;

        setLoadingPreview(true);
        try {
            const userId = window.localStorage.getItem('userID');
            
            // Use batch processing for much faster preview calculation
            const recipeConfigs = selectedRecipes.map(recipe => ({
                recipeId: recipe.recipeId,
                servings: recipe.servings
            }));

            const response = await axiosInstance.post('/meal-planning/calculate-batch-cost', {
                recipes: recipeConfigs,
                userId: userId
            });

            if (response.data.success && response.data.mealPlanTotals) {
                setPreviewData({
                    recipeCount: response.data.mealPlanTotals.recipeCount,
                    totalServings: response.data.mealPlanTotals.totalServings,
                    totalPrice: response.data.mealPlanTotals.totalPrice,
                    averagePricePerServing: response.data.mealPlanTotals.averagePricePerServing
                });
            } else {
                // Fallback to individual calculations if batch fails
                let totalPrice = 0;
                let totalServings = 0;
                let recipeCount = selectedRecipes.length;
                
                for (const recipe of selectedRecipes) {
                    totalServings += recipe.servings;
                    
                    try {
                        const recipeResponse = await axiosInstance.get(`/meal-planning/recipe/${recipe.recipeId}`, {
                            params: {
                                servings: recipe.servings
                            }
                        });
                        
                        if (recipeResponse.data.success && recipeResponse.data.recipe.pricing) {
                            totalPrice += recipeResponse.data.recipe.pricing.totalPrice;
                        }
                    } catch (err) {
                        console.error(`Error fetching cost for recipe ${recipe.recipeId}:`, err);
                    }
                }
                
                setPreviewData({
                    recipeCount,
                    totalServings,
                    totalPrice,
                    averagePricePerServing: totalServings > 0 ? totalPrice / totalServings : 0
                });
            }
        } catch (error) {
            console.error('Error loading preview:', error);
        } finally {
            setLoadingPreview(false);
        }
    };

    const loadNutritionPreview = async () => {
        if (selectedRecipes.length === 0) return;

        setLoadingNutritionPreview(true);
        setNutritionPreview(null);
        try {
            const totalServings = selectedRecipes.reduce((sum, r) => sum + (r.servings || 0), 0);
            const results = await Promise.allSettled(
                selectedRecipes.map((recipe) =>
                    axiosInstance.post(`/recipes/${recipe.recipeId}/calculate-nutrition`, {
                        servings: recipe.servings
                    })
                )
            );

            const totals = {
                Calories: 0,
                Protein: 0,
                Carbs: 0,
                Fats: 0,
                Sodium: 0,
                Sugar: 0,
                Fiber: 0
            };

            results.forEach((result) => {
                if (result.status === 'fulfilled' && result.value?.data?.total) {
                    const t = result.value.data.total;
                    totals.Calories += t.Calories || 0;
                    totals.Protein += t.Protein || 0;
                    totals.Carbs += t.Carbs || 0;
                    totals.Fats += t.Fats || 0;
                    totals.Sodium += t.Sodium || 0;
                    totals.Sugar += t.Sugar || 0;
                    totals.Fiber += t.Fiber || 0;
                }
            });

            setNutritionPreview({
                total: totals,
                perServing: totalServings > 0 ? {
                    Calories: Math.round(totals.Calories / totalServings),
                    Protein: parseFloat((totals.Protein / totalServings).toFixed(1)),
                    Carbs: parseFloat((totals.Carbs / totalServings).toFixed(1)),
                    Fats: parseFloat((totals.Fats / totalServings).toFixed(1)),
                    Sodium: Math.round(totals.Sodium / totalServings),
                    Sugar: parseFloat((totals.Sugar / totalServings).toFixed(1)),
                    Fiber: parseFloat((totals.Fiber / totalServings).toFixed(1))
                } : null,
                totalServings
            });
        } catch (error) {
            console.error('Error loading nutrition preview:', error);
        } finally {
            setLoadingNutritionPreview(false);
        }
    };

    const saveMealPlan = async () => {
        if (selectedRecipes.length === 0) {
            alert('Please add at least one recipe to your meal plan');
            return;
        }

        try {
            const payload = {
                name: mealPlanName.trim() || undefined,
                description: mealPlanDescription.trim() || '',
                startDate: startDate || undefined,
                endDate: endDate || undefined,
                recipes: selectedRecipes
            };
            const response = await axiosInstance.post('/meal-planning/plans', payload);

            if (response.data.success) {
                alert('Meal plan saved successfully!');
                setMealPlanName('');
                setMealPlanDescription('');
                setStartDate('');
                setEndDate('');
                setSelectedRecipes([]);
                setSearchResults([]);
                setSearchQuery('');
                setActiveTab('view');
            }
        } catch (error) {
            console.error('Error saving meal plan:', error);
            alert('Failed to save meal plan');
        }
    };

    const deleteMealPlan = async (planId) => {
        if (!window.confirm('Are you sure you want to delete this meal plan?')) {
            return;
        }

        try {
            const response = await axiosInstance.delete(`/meal-planning/plans/${planId}`);
            if (response.data.success) {
                alert('Meal plan deleted successfully');
                loadMealPlans();
            }
        } catch (error) {
            console.error('Error deleting meal plan:', error);
            alert('Failed to delete meal plan');
        }
    };

    const viewMealPlanDetails = (planId) => {
        navigate(`/meal-plan/${planId}`);
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* "Added To Meal Plan" toast - shows for 2s when adding a recipe from draft */}
            {addedToPlanToast && (
                <div
                    className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-lg bg-gray-900 text-white text-sm font-medium shadow-lg"
                    role="status"
                    aria-live="polite"
                >
                    Added To Meal Plan
                </div>
            )}

            <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
                {/* Compact header - Mob style */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">Meal Planning</h1>
                        <p className="text-sm text-gray-600 mt-0.5">Plan your meals, save time and money</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => navigate('/meal-planning/ai')}
                            className="px-3 py-2 rounded-full text-sm font-medium bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200 transition-colors"
                        >
                            AI Meal Planning
                        </button>
                        <button
                            onClick={() => navigate('/meal-planning/preferences')}
                            className="px-3 py-2 rounded-full text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
                        >
                            Preferences
                        </button>
                    </div>
                </div>

                {/* Tabs - pill style like Mob */}
                <div className="flex gap-1 p-1 bg-white rounded-full shadow-sm border border-gray-100 mb-4 sm:mb-6">
                    <button
                        onClick={() => setActiveTab('create')}
                        className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-colors ${activeTab === 'create' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                        Create
                    </button>
                    <button
                        onClick={() => setActiveTab('view')}
                        className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-colors ${activeTab === 'view' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                        My Plans
                    </button>
                    <button
                        onClick={() => setActiveTab('community')}
                        className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-colors ${activeTab === 'community' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                        Community
                    </button>
                </div>

            {activeTab === 'create' && (
                <div className="space-y-4 sm:space-y-5">
                    {/* AI CTA - compact banner */}
                    <div className="rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 border border-amber-200 p-4 sm:p-5">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div>
                                <h2 className="text-base sm:text-lg font-semibold text-amber-900">Try AI-Powered Meal Planning</h2>
                                <p className="text-sm text-amber-800 mt-1">Tell us how many people and days — we&apos;ll create your plan.</p>
                            </div>
                            <button
                                onClick={() => navigate('/meal-planning/ai')}
                                className="shrink-0 px-4 py-2 rounded-full text-sm font-medium bg-white text-amber-800 border border-amber-300 hover:bg-amber-50 transition-colors"
                            >
                                Get Started with AI →
                            </button>
                        </div>
                    </div>

                    {/* Meal prep draft - compact section */}
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 sm:p-4 border-b border-gray-100">
                            <div>
                                <h2 className="text-sm font-semibold text-gray-900">Meal prep draft</h2>
                                <p className="text-xs text-gray-500 mt-0.5">Recipes you saved — add them to this plan</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => navigate('/meal-prep-draft')}
                                className="text-xs font-medium text-gray-600 hover:text-gray-900 shrink-0"
                            >
                                View all →
                            </button>
                        </div>
                        <div className="p-3 sm:p-4">
                            {loadingMealPrepDraft ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                                    <span className="ml-2 text-sm text-gray-500">Loading...</span>
                                </div>
                            ) : mealPrepDraftRecipes.length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-6">No draft recipes yet. Use the + on any recipe and choose &quot;Add to Meal prep draft&quot;.</p>
                            ) : (
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                                    {mealPrepDraftRecipes.map(recipe => (
                                        <div key={recipe._id} className="group rounded-lg border border-gray-100 overflow-hidden bg-gray-50 hover:border-gray-200 transition-colors">
                                            <div className="aspect-[4/3] overflow-hidden bg-gray-200">
                                                <img src={recipe.imageUrl} alt={recipe.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
                                            </div>
                                            <div className="p-2 sm:p-2.5">
                                                <h3 className="text-xs sm:text-sm font-medium text-gray-900 line-clamp-2 leading-tight">{recipe.name}</h3>
                                                <p className="text-xs text-gray-500 mt-0.5">{recipe.servings || 1} servings · {recipe.cookingTime} min</p>
                                                <button
                                                    onClick={() => addRecipeToMealPlan(recipe)}
                                                    className="mt-2 w-full py-1.5 rounded-lg text-xs font-medium bg-gray-900 text-white hover:bg-gray-800 transition-colors"
                                                >
                                                    Add to plan
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Meal plan details - compact form */}
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                        <div className="p-3 sm:p-4 border-b border-gray-100">
                            <h2 className="text-sm font-semibold text-gray-900">Meal plan details</h2>
                            <p className="text-xs text-gray-500 mt-0.5">Name and dates</p>
                        </div>
                        <div className="p-3 sm:p-4 space-y-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Plan name <span className="text-gray-400 font-normal">(optional — auto-named if blank)</span></label>
                                <input
                                    type="text"
                                    value={mealPlanName}
                                    onChange={(e) => setMealPlanName(e.target.value)}
                                    placeholder="e.g. Weekly Meal Plan"
                                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                                <textarea
                                    value={mealPlanDescription}
                                    onChange={(e) => setMealPlanDescription(e.target.value)}
                                    placeholder="Optional..."
                                    rows={2}
                                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Start date <span className="text-gray-400 font-normal">(optional)</span></label>
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">End date <span className="text-gray-400 font-normal">(optional)</span></label>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                    />
                                </div>
                            </div>
                            {(allergens.length > 0 || dislikedFoods.length > 0) && (
                                <div className="p-2 rounded-lg bg-amber-50 border border-amber-100">
                                    <p className="text-xs text-amber-800">{allergens.length} allergen(s), {dislikedFoods.length} disliked — recipes filtered.</p>
                                    <button type="button" onClick={() => navigate('/meal-planning/preferences')} className="text-xs font-medium text-amber-700 mt-1 hover:underline">Edit preferences</button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Find recipes - compact search */}
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                        <div className="p-3 sm:p-4 border-b border-gray-100">
                            <h2 className="text-sm font-semibold text-gray-900">Find recipes</h2>
                            <p className="text-xs text-gray-500 mt-0.5">Search to add to your plan</p>
                        </div>
                        <div className="p-3 sm:p-4 space-y-3">
                            <div className="flex flex-wrap gap-1.5">
                                <button
                                    onClick={() => setSearchType('name')}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${searchType === 'name' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                >
                                    By name
                                </button>
                                <button
                                    onClick={() => setSearchType('ingredient')}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${searchType === 'ingredient' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                >
                                    By ingredient
                                </button>
                                <button
                                    onClick={searchFromCupboard}
                                    disabled={searching}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${searchType === 'cupboard' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} disabled:opacity-50`}
                                >
                                    From cupboard
                                </button>
                            </div>
                            {searchType !== 'cupboard' && (
                                <div className="flex gap-2">
                                    {searchType === 'ingredient' ? (
                                        <div className="flex-1 min-w-0">
                                            <IngredientAutocomplete value={searchQuery} onChange={setSearchQuery} placeholder="Ingredient..." style={{ flex: 1 }} />
                                        </div>
                                    ) : (
                                        <input
                                            type="text"
                                            className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && searchRecipes()}
                                            placeholder="Search recipe by name..."
                                        />
                                    )}
                                    <button
                                        onClick={searchRecipes}
                                        disabled={searching}
                                        className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 shrink-0"
                                    >
                                        {searching ? '...' : 'Search'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Search Results - compact grid */}
                    {searchResults.length > 0 && (
                        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                            <div className="p-3 sm:p-4 border-b border-gray-100">
                                <h2 className="text-sm font-semibold text-gray-900">Search results ({searchResults.length})</h2>
                            </div>
                            <div className="p-3 sm:p-4">
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                                    {searchResults.map(recipe => (
                                        <div key={recipe._id} className="rounded-lg border border-gray-100 overflow-hidden bg-gray-50 hover:border-gray-200 transition-colors group">
                                            <div className="aspect-[4/3] overflow-hidden bg-gray-200">
                                                <img src={recipe.imageUrl} alt={recipe.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
                                            </div>
                                            <div className="p-2 sm:p-2.5">
                                                <h3 className="text-xs sm:text-sm font-medium text-gray-900 line-clamp-2 leading-tight">{recipe.name}</h3>
                                                <p className="text-xs text-gray-500 mt-0.5">{recipe.servings} servings · {recipe.cookingTime} min</p>
                                                {recipe.costCalculation && (
                                                    <p className="text-xs text-green-600 mt-0.5">R{(recipe.costCalculation.totalCost / (recipe.servings || 1)).toFixed(2)}/serving</p>
                                                )}
                                                <button
                                                    onClick={() => addRecipeToMealPlan(recipe)}
                                                    className="mt-2 w-full py-1.5 rounded-lg text-xs font-medium bg-gray-900 text-white hover:bg-gray-800 transition-colors"
                                                >
                                                    Add to plan
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Selected recipes + summary + save - compact */}
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                        <div className="p-3 sm:p-4 border-b border-gray-100">
                            <h2 className="text-sm font-semibold text-gray-900">Selected recipes ({selectedRecipes.length})</h2>
                            <p className="text-xs text-gray-500 mt-0.5">Manage servings and meal type</p>
                        </div>
                        <div className="p-3 sm:p-4 space-y-3">
                            {selectedRecipes.length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-6">No recipes yet. Add from draft or search above.</p>
                            ) : (
                                <div className="space-y-2">
                                    {selectedRecipes.map(recipe => (
                                        <div key={recipe.recipeId} className="flex gap-3 p-2 rounded-lg border border-gray-100 bg-gray-50">
                                            <img src={recipe.recipeImage} alt={recipe.recipeName} className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg object-cover shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <h4 className="text-sm font-medium text-gray-900 truncate">{recipe.recipeName}</h4>
                                                <div className="flex flex-wrap gap-2 mt-1.5">
                                                    <div>
                                                        <label className="sr-only">Servings</label>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            value={recipe.servings}
                                                            onChange={(e) => updateRecipeServings(recipe.recipeId, e.target.value)}
                                                            className="w-12 px-1.5 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-amber-500"
                                                        />
                                                        <span className="text-xs text-gray-500 ml-1">servings</span>
                                                    </div>
                                                    <select
                                                        value={recipe.mealType}
                                                        onChange={(e) => updateRecipeMealType(recipe.recipeId, e.target.value)}
                                                        className="text-xs border border-gray-200 rounded px-2 py-1 focus:ring-1 focus:ring-amber-500"
                                                    >
                                                        <option value="breakfast">Breakfast</option>
                                                        <option value="lunch">Lunch</option>
                                                        <option value="dinner">Dinner</option>
                                                        <option value="snack">Snack</option>
                                                    </select>
                                                </div>
                                                <button onClick={() => removeRecipeFromMealPlan(recipe.recipeId)} className="mt-1.5 text-xs text-red-600 hover:underline">Remove</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {previewData && (
                                <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                                        <div><div className="text-sm font-semibold text-gray-900">{previewData.recipeCount}</div><div className="text-xs text-gray-500">Recipes</div></div>
                                        <div><div className="text-sm font-semibold text-gray-900">{previewData.totalServings}</div><div className="text-xs text-gray-500">Servings</div></div>
                                        <div><div className="text-sm font-semibold text-gray-900">R{previewData.totalPrice.toFixed(2)}</div><div className="text-xs text-gray-500">Total</div></div>
                                        <div><div className="text-sm font-semibold text-gray-900">R{previewData.averagePricePerServing.toFixed(2)}</div><div className="text-xs text-gray-500">Per serving</div></div>
                                    </div>
                                    {loadingNutritionPreview && (
                                        <div className="flex items-center justify-center gap-2 py-2 text-xs text-gray-500">
                                            <div className="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                                            Loading nutrition…
                                        </div>
                                    )}
                                    {!loadingNutritionPreview && nutritionPreview && nutritionPreview.total && (
                                        <div className="pt-3 border-t border-gray-100">
                                            <div className="text-xs font-medium text-gray-600 mb-2">Nutrition (total plan)</div>
                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                                                <div><div className="text-sm font-semibold text-gray-900">{formatNutritionValue('Calories', nutritionPreview.total.Calories)}</div><div className="text-xs text-gray-500">Calories</div></div>
                                                <div><div className="text-sm font-semibold text-gray-900">{formatNutritionValue('Protein', nutritionPreview.total.Protein)}</div><div className="text-xs text-gray-500">Protein</div></div>
                                                <div><div className="text-sm font-semibold text-gray-900">{formatNutritionValue('Carbs', nutritionPreview.total.Carbs)}</div><div className="text-xs text-gray-500">Carbs</div></div>
                                                <div><div className="text-sm font-semibold text-gray-900">{formatNutritionValue('Fats', nutritionPreview.total.Fats)}</div><div className="text-xs text-gray-500">Fats</div></div>
                                            </div>
                                            {nutritionPreview.perServing && (
                                                <div className="text-xs text-gray-500 mt-2 text-center">Per serving: {formatNutritionValue('Calories', nutritionPreview.perServing.Calories)} · {formatNutritionValue('Protein', nutritionPreview.perServing.Protein)} · {formatNutritionValue('Carbs', nutritionPreview.perServing.Carbs)} · {formatNutritionValue('Fats', nutritionPreview.perServing.Fats)}</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className="mt-4 pt-4 border-t border-gray-100">
                                <button
                                    onClick={saveMealPlan}
                                    className="w-full py-3 rounded-xl text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800 transition-colors"
                                >
                                    Save meal plan
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* View tab - compact list */}
            {activeTab === 'view' && (
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="p-3 sm:p-4 border-b border-gray-100">
                        <h2 className="text-sm font-semibold text-gray-900">My meal plans</h2>
                        <p className="text-xs text-gray-500 mt-0.5">View or delete plans</p>
                    </div>
                    <div className="p-3 sm:p-4">
                        {loadingPlans ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                                <span className="ml-2 text-sm text-gray-500">Loading...</span>
                            </div>
                        ) : mealPlans.length === 0 ? (
                            <p className="text-sm text-gray-500 text-center py-8">No meal plans yet. Create your first one.</p>
                        ) : (
                            <div className="space-y-2">
                                {mealPlans.map(plan => (
                                    <div key={plan._id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors">
                                        <div className="min-w-0">
                                            <h3 className="text-sm font-semibold text-gray-900 truncate">{plan.name}</h3>
                                            <p className="text-xs text-gray-500 mt-0.5">
                                                {new Date(plan.startDate).toLocaleDateString()} – {new Date(plan.endDate).toLocaleDateString()} · {plan.recipes.length} recipes · R{plan.totalEstimatedPrice?.toFixed(2) || '0.00'}
                                            </p>
                                        </div>
                                        <div className="flex gap-2 shrink-0">
                                            <button onClick={() => viewMealPlanDetails(plan._id)} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-900 text-white hover:bg-gray-800">View</button>
                                            <button onClick={() => deleteMealPlan(plan._id)} className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 border border-gray-200 hover:bg-gray-50">Delete</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Community tab - search public meal plans */}
            {activeTab === 'community' && (
                <div className="space-y-4">
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                        <div className="p-3 sm:p-4 border-b border-gray-100">
                            <h2 className="text-sm font-semibold text-gray-900">Community meal plans</h2>
                            <p className="text-xs text-gray-500 mt-0.5">Browse meal plans shared by other users</p>
                        </div>
                        <div className="p-3 sm:p-4">
                            <form onSubmit={handlePublicSearch} className="flex gap-2 mb-4">
                                <input
                                    type="text"
                                    value={publicSearchQuery}
                                    onChange={(e) => setPublicSearchQuery(e.target.value)}
                                    placeholder="Search by name or description…"
                                    className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                />
                                <button
                                    type="submit"
                                    disabled={loadingPublicPlans}
                                    className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 shrink-0"
                                >
                                    {loadingPublicPlans ? (
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    ) : 'Search'}
                                </button>
                            </form>

                            {loadingPublicPlans ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                                    <span className="ml-2 text-sm text-gray-500">Loading...</span>
                                </div>
                            ) : publicPlans.length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-8">
                                    {publicPlansLoaded ? 'No public meal plans found. Try a different search.' : 'Search for community meal plans above.'}
                                </p>
                            ) : (
                                <div className="space-y-2">
                                    {publicPlans.map(plan => (
                                        <div key={plan._id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <h3 className="text-sm font-semibold text-gray-900 truncate">{plan.name}</h3>
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-100">
                                                        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        Public
                                                    </span>
                                                </div>
                                                <p className="text-xs text-gray-500 mt-0.5">
                                                    {plan.recipes?.length || 0} recipes
                                                    {plan.userId?.name || plan.userId?.username ? ` · by ${plan.userId.name || plan.userId.username}` : ''}
                                                    {plan.description ? ` · ${plan.description.slice(0, 60)}${plan.description.length > 60 ? '…' : ''}` : ''}
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => navigate(`/meal-plan/${plan._id}`)}
                                                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-900 text-white hover:bg-gray-800 shrink-0"
                                            >
                                                View
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            </div>
        </div>
    );
};

export default MealPlanningNew;

